import {Link, useNavigate, useParams} from 'react-router-dom';
import {useSpecies} from '../api/plants';
import {useMe} from '../hooks/useAuth';
import Button from '../components/Button';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonBlock, SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import PlantRow from '../components/plant/PlantRow';
import SeasonStory, {useSeasonFeed} from '../components/plant/SeasonStory';
import {plantSubtitle, varietiesLabel} from '../lib/plants';
import {formatDayMonth} from '../lib/when';

// Every variety of one plant name (wireframe "species"): the plant page's season view over all
// of them together ("what did the garlic do this year?"), then the varieties, each opening its
// own page, where rename and the permanent toggle live. Nothing is stored for a species; it is
// the plants that share a name.

const tellsWord = (n: number) => `${n} ${n === 1 ? 'tell' : 'tells'}`;

export default function SpeciesPage() {
    const {key} = useParams<{key: string}>();
    const navigate = useNavigate();
    const me = useMe();
    const detail = useSpecies(key);
    const data = detail.data;
    const state = useSeasonFeed(data?.seasons ?? [], {species: key});
    const firstPlace = state.posts.find(p => p.place)?.place ?? null;
    // One timeline a year when every variety is a tree or bush (apples), else the crop lifecycle.
    const isPermanent = !!data && data.plants.every(p => p.isPermanent);
    const varieties = data ? [...data.plants].sort((a, b) => ((a.latest?.whenLocal ?? a.lastAt ?? '') < (b.latest?.whenLocal ?? b.lastAt ?? '') ? 1 : -1)) : [];

    const addTell = () => {
        if (!data) return;
        const q = new URLSearchParams({plant: data.name});
        if (firstPlace) q.set('place', firstPlace.id);
        navigate(`/tell/new?${q.toString()}`);
    };

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="pt-2">
                <Link to="/plants" className="meta mb-1 inline-flex items-center gap-1 no-underline">
                    <Icon name="back" size={16} /> Plants
                </Link>
                {data ? (
                    <>
                        <h1 className="display m-0">{data.name}</h1>
                        <p className="meta mt-1">{varietiesLabel(data.plants.length)} · {tellsWord(data.plants.reduce((n, p) => n + p.tellCount, 0))}</p>
                    </>
                ) : (
                    <SkeletonBlock className="h-9 w-1/2" />
                )}
            </header>

            {detail.isPending && <SkeletonList rows={4} />}
            {detail.isError && <ErrorBox error={detail.error} onRetry={() => void detail.refetch()} />}

            {detail.isSuccess && state.seasons.length === 0 && (
                <EmptyState icon="leaf" tone="plain" title="Nothing recorded for these yet.">
                    Their tells will line up here season by season.
                </EmptyState>
            )}

            {data && (
                <SeasonStory
                    state={state}
                    isPermanent={isPermanent}
                    summary={season => [firstPlace ? null : season.placeName, `started ${formatDayMonth(season.firstAt)}`, tellsWord(season.tells)].filter(Boolean).join(' · ')}
                    placeLink={firstPlace}
                />
            )}

            {varieties.length > 0 && (
                <section className="flex flex-col gap-2">
                    <h2 className="band-label m-0 text-ink-2">Varieties</h2>
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface px-4">
                        {varieties.map(p => (
                            <PlantRow key={p.id} plant={p} title={p.variety ?? `${p.name}, no variety named`} subtitle={plantSubtitle(p, me.data?.hemisphere)} />
                        ))}
                    </div>
                </section>
            )}

            {data && (
                <Button variant="secondary" onClick={addTell}>
                    <Icon name="plus" size={20} /> Add a tell
                </Button>
            )}
        </div>
    );
}
